import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colorsV2 } from 'style/colors-v2';
import { Typography } from 'components/DesignSystemV2/Typography';
import { useKeenSlider } from 'keen-slider/react';
import { SectionV3 } from 'components/SectionV3';
import { ResponsiveContainer } from 'components/ResponsiveContainer';
import QuoteIcon from 'components/Icon/SVGIcons/QuoteIcon';
import { LazyImage } from 'components/LazyImage';
import { imageFitStyle } from 'style/css-style';
import { toScreen } from 'utils/media-query/responsive.util';
import { desktopFirstV3 } from 'style/breakpointsv3';

const gradients = [
  {
    inner: '#F8F6FE',
    outer: '#D5CBF6'
  },
  {
    inner: '#F6F8FE',
    outer: '#CAD8F7'
  },
  {
    inner: '#FEF6F6',
    outer: '#FAD6D6'
  },
  {
    inner: '#F3FBF5',
    outer: '#CAECD1'
  },
  {
    inner: '#FCF3F4',
    outer: '#F5D1D6'
  }
];

const Section = styled(SectionV3)`
  padding: 0;
`;

const SliderItemContent = styled.div`
  display: flex;
  gap: 80px;
  max-width: 900px;
  margin: 0 auto;
  padding: 100px 0 108px;

  ${toScreen(desktopFirstV3.sm)} {
    flex-direction: column;
    align-items: center;
    gap: 40px;
  }
`;

const LeftContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const RightContent = styled.div`
  flex-basis: 212px;
  padding-right: 16px;

  ${toScreen(desktopFirstV3.sm)} {
    width: 100%;
    max-width: 212px;
    margin: 0 auto;
  }
`;

const SliderWrapper = styled.div`
  position: relative;
  max-width: 900px;
  margin: 0 auto;
`;

const ImageWrapper = styled.div`
  position: relative;
  aspect-ratio: 212/285;
  max-width: 212px;
  margin: 0 auto;
  border-radius: 20px;
  border: 4px solid ${colorsV2.white100};
  box-shadow: 0px 24px 32px 0px #3042780f, 0px 16px 24px 0px #3042780f,
    0px 4px 8px 0px #3042780f;
  transform: rotateZ(2.82deg);
  background: radial-gradient(
    70.4% 50.14% at 48.95% 50.14%,
    ${({ $index }) => gradients[$index].inner} 0%,
    ${({ $index }) => gradients[$index].outer} 100%
  );

  img {
    ${imageFitStyle('contain')}
    max-height: 85%;
    top: initial;
    bottom: 0;
  }
`;

const Score = styled(Typography)`
  display: inline-block;
  position: absolute;
  left: 0;
  bottom: 15%;
  background: ${colorsV2.white100};
  padding: 10px 18px;
  border-radius: 16px;
  white-space: nowrap;
  transform: rotateZ(-12deg) translateX(-50%);
  transform-origin: center;
  box-shadow: 0px 12px 24px 0px #30427814, 0px 3px 6px 0px #3042780f;

  ${toScreen(desktopFirstV3.sm)} {
    left: 50%;
  }
`;

const DotWrapper = styled.div`
  display: flex;
  gap: 8px;
  position: absolute;
  bottom: 90px;
  left: 0;
`;

const Dot = styled.button`
  outline: none;
  border: none;
  background-color: ${({ $active }) =>
    $active ? colorsV2.primary100 : colorsV2.neutral20};
  width: 8px;
  height: 8px;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
`;

const LinearthinkingQuote = ({ data }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const intervalRef = useRef(-1);
  const [sliderRef, slider] = useKeenSlider({
    slidesPerView: 1,
    spacing: 8,
    loop: true,
    duration: 2000,
    mode: 'snap',
    slideChanged: e => {
      setSelectedIndex(e.details().relativeSlide);
    }
  });

  const autoPlay = useCallback(() => {
    intervalRef.current = setInterval(slider?.next, 3000);
  }, [slider]);

  useEffect(() => {
    // autoPlay();

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [autoPlay]);

  return (
    <Section>
      <ResponsiveContainer>
        <SliderWrapper ref={sliderRef} className="keen-slider">
          {data.map(({ name, score, school, quote, image }, idx) => (
            <div key={idx} className="keen-slider__slide">
              <SliderItemContent>
                <LeftContent>
                  <QuoteIcon color={colorsV2.neutral40} />
                  <Typography
                    variant="medium/20-28"
                    tabletVariant="medium/28-36"
                    desktopVariant="medium/32-40"
                    color={colorsV2.neutral180}
                    v3
                  >
                    {quote}
                  </Typography>
                  <div>
                    <Typography
                      variant="semi-bold/16-24"
                      color={colorsV2.neutral180}
                    >
                      {name}
                    </Typography>
                    <Typography
                      variant="regular/14-20"
                      color={colorsV2.neutral120}
                    >
                      {school}
                    </Typography>
                  </div>
                </LeftContent>
                <RightContent>
                  <ImageWrapper $index={idx}>
                    <LazyImage handle={image} />
                    <Score
                      variant="bold/20-28"
                      color={colorsV2.primary100}
                      type="span"
                    >
                      {score}
                    </Score>
                  </ImageWrapper>
                </RightContent>
              </SliderItemContent>
            </div>
          ))}
          <DotWrapper>
            {data.map((_, idx) => (
              <Dot
                key={idx}
                $active={idx === selectedIndex}
                onClick={() => {
                  slider.moveToSlide(idx);
                }}
              />
            ))}
          </DotWrapper>
        </SliderWrapper>
      </ResponsiveContainer>
    </Section>
  );
};

export default LinearthinkingQuote;
